import {ActionList, ActionMenu, Button} from '@primer/react'
import {TriangleDownIcon} from '@primer/octicons-react'

import {useSearchParams} from '@github-ui/use-navigate'

import type {ResourceTypeInfo} from '../../../../types'

// URL search param holding the selected resource type filters as a
// comma-separated list of labels, e.g. ?type=MCP server,Skill. Filtering is
// applied server-side (the Rails adapter narrows the catalog pool before
// paginating), so the route refetches when this param changes.
export const RESOURCE_TYPE_FILTER_PARAM = 'type'

// Fallback list used only when the backend types endpoint returns nothing, so
// the filter still works (without counts) during transient upstream failures.
const FALLBACK_TYPES: ResourceTypeInfo[] = [
  {label: 'MCP server', media_type: '', count: 0},
  {label: 'Skill', media_type: '', count: 0},
  {label: 'Agent', media_type: '', count: 0},
  {label: 'A2A agent', media_type: '', count: 0},
]

// Parses the comma-separated `type` param into a list of selected labels.
export function getSelectedResourceTypes(searchParams: URLSearchParams): string[] {
  const raw = searchParams.get(RESOURCE_TYPE_FILTER_PARAM) || ''
  return raw ? raw.split(',').filter(Boolean) : []
}

type Props = {
  types?: ResourceTypeInfo[]
}

export function ResourceTypeFilter({types}: Props) {
  const [searchParams, setSearchParams] = useSearchParams()
  const selected = getSelectedResourceTypes(searchParams)

  const options = types && types.length > 0 ? types : FALLBACK_TYPES
  // Counts are only meaningful when they came from the backend types endpoint.
  const showCounts = Boolean(types && types.length > 0)

  const handleSelect = (event: React.MouseEvent | React.KeyboardEvent, value: string) => {
    // Prevent ActionList.Item's afterSelect from closing the menu so multiple
    // types can be toggled in a single interaction.
    event.preventDefault()

    const next = selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]

    const params = new URLSearchParams(searchParams)
    if (next.length > 0) {
      params.set(RESOURCE_TYPE_FILTER_PARAM, next.join(','))
    } else {
      params.delete(RESOURCE_TYPE_FILTER_PARAM)
    }
    // Reset to the first page so the filtered view starts from the top.
    params.delete('page')

    setSearchParams(params, {preventAutofocus: true})
  }

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <Button
          trailingVisual={TriangleDownIcon}
          count={selected.length || undefined}
          data-testid="resource-type-filter"
        >
          Resource type
        </Button>
      </ActionMenu.Anchor>

      <ActionMenu.Overlay width="auto">
        <ActionList selectionVariant="multiple" aria-label="Filter by resource type">
          {options.map(option => (
            <ActionList.Item
              key={option.label}
              selected={selected.includes(option.label)}
              onSelect={event => handleSelect(event, option.label)}
            >
              {option.label}
              {showCounts && <ActionList.TrailingVisual>{option.count.toLocaleString()}</ActionList.TrailingVisual>}
            </ActionList.Item>
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
